import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Calculator } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { SectionCard } from '../../../components/design-system/admin/SectionCard';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { DealCalculator, DealDraft } from './DealCalculator';
import { money } from './rate-card';

type Status = 'draft' | 'active' | 'ended';

interface ContractForm {
  status: Status;
  monthlyAmount: number;
  currency: string;
  billingInterval: 'month' | 'year';
  termMonths: number;
  startDate: string;
  autoRenew: boolean;
  noticeDays: number;
  integrationFee: number;
  discountPercent: number;
  notes: string;
  lines: { key: string; label?: string; qty: number; unitPrice: number }[];
  limits?: Record<string, number>;
}

const EMPTY: ContractForm = {
  status: 'draft',
  monthlyAmount: 0,
  currency: 'usd',
  billingInterval: 'year',
  termMonths: 12,
  startDate: '',
  autoRenew: true,
  noticeDays: 60,
  integrationFee: 0,
  discountPercent: 0,
  notes: '',
  lines: [],
};

const labelClass = 'text-xs text-muted-foreground';

const addMonths = (iso: string, months: number) => {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/**
 * A vendor's sales-led agreement (docs/pricing-v2.md): price, term (12 months
 * minimum), renewal and notice. Priced from the rate card through the deal
 * calculator, so every contract can be explained line by line. Saving it
 * active sets what renewal invoices bill and the volumes its packs bought.
 */
export function VendorContractCard({ vendorId }: { vendorId: string }) {
  const { t, i18n } = useTranslation();
  const plansApi = usePlansApi();
  const [form, setForm] = useState<ContractForm>(EMPTY);
  const [saved, setSaved] = useState<ContractForm | null>(null);
  const [calcOpen, setCalcOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.resolve(plansApi.getVendorContract(vendorId))
      .then((res: any) => {
        const c = res?.contract;
        if (!c) return;
        const loaded: ContractForm = {
          ...EMPTY,
          ...c,
          startDate: c.startDate ? String(c.startDate).slice(0, 10) : '',
          notes: c.notes || '',
          lines: c.lines || [],
        };
        setForm(loaded);
        setSaved(loaded);
      })
      .catch(() => { /* no contract yet */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const set = <K extends keyof ContractForm>(k: K, v: ContractForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify(saved ?? EMPTY);
  const endDate = form.startDate ? addMonths(form.startDate, form.termMonths) : '';
  const tooShort = form.termMonths < 12;

  const save = async () => {
    setSaving(true);
    try {
      const res: any = await plansApi.saveVendorContract(vendorId, { ...form, startDate: form.startDate || null });
      const c = res?.contract;
      const next = { ...form, startDate: c?.startDate ? String(c.startDate).slice(0, 10) : form.startDate };
      setForm(next);
      setSaved(next);
      toast.success(t('PRICING.CONTRACT.SAVED'));
    } catch (e: any) {
      let message = t('PRICING.CONTRACT.ERROR');
      try {
        const body = typeof e?.json === 'function' ? await e.json() : null;
        if (body?.message) message = String(body.message);
      } catch { /* keep the generic message */ }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const fromCalculator = (deal: any, draft: DealDraft) => {
    const integration = deal.lines.find((l: any) => l.key === 'integration');
    setForm((f) => ({
      ...f,
      monthlyAmount: deal.monthlyAmount,
      termMonths: draft.termMonths,
      discountPercent: deal.salesDiscountPercent,
      integrationFee: integration ? integration.total : f.integrationFee,
      lines: deal.lines.map((l: any) => ({ key: l.key, label: l.label, qty: l.qty, unitPrice: l.unitPrice })),
      limits: deal.limits,
    }));
    setCalcOpen(false);
  };

  const initialDraft: DealDraft = {
    qty: Object.fromEntries(form.lines.map((l) => [l.key, l.qty])),
    termMonths: form.termMonths || 12,
    salesDiscountPercent: form.discountPercent || 0,
  };

  return (
    <SectionCard
      title={t('PRICING.CONTRACT.TITLE')}
      description={t('PRICING.CONTRACT.DESC')}
      testId="vendor-contract-card"
      action={
        <Button variant="outline" size="sm" onClick={() => setCalcOpen(true)} data-testid="contract-open-calculator">
          <Calculator className="size-3.5" />
          {t('PRICING.CONTRACT.PRICE_IT')}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <Label className={labelClass}>{t('PRICING.CONTRACT.STATUS')}</Label>
          <ToggleGroup type="single" variant="outline" value={form.status} onValueChange={(v) => v && set('status', v as Status)} data-testid="contract-status">
            {(['draft', 'active', 'ended'] as Status[]).map((s) => (
              <ToggleGroupItem key={s} value={s} className="px-4" data-testid={`contract-status-${s}`}>
                {t(`PRICING.CONTRACT.STATUS_${s.toUpperCase()}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-amount" className={labelClass}>{t('PRICING.CONTRACT.MONTHLY')}</Label>
            <Input id="c-amount" type="number" min={0} className="tabular-nums" value={form.monthlyAmount} onChange={(e) => set('monthlyAmount', parseFloat(e.target.value) || 0)} data-testid="contract-amount" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-term" className={labelClass}>{t('PRICING.CONTRACT.TERM')}</Label>
            <Input id="c-term" type="number" min={12} step={12} aria-invalid={tooShort || undefined} className="tabular-nums" value={form.termMonths} onChange={(e) => set('termMonths', parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-currency" className={labelClass}>{t('PLANS.ADMIN.FORM.CURRENCY')}</Label>
            <Input id="c-currency" className="uppercase" value={form.currency} onChange={(e) => set('currency', e.target.value.toLowerCase())} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-start" className={labelClass}>{t('PRICING.CONTRACT.START')}</Label>
            <Input id="c-start" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className={labelClass}>{t('PRICING.CONTRACT.END')}</Label>
            <p className="flex h-9 items-center text-sm tabular-nums">{endDate ? new Date(endDate).toLocaleDateString(i18n.language) : '—'}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className={labelClass}>{t('PRICING.CONTRACT.INVOICED')}</Label>
            <ToggleGroup type="single" variant="outline" value={form.billingInterval} onValueChange={(v) => v && set('billingInterval', v as 'month' | 'year')}>
              <ToggleGroupItem value="year" className="px-3">{t('PRICING.UPGRADE.YEARLY')}</ToggleGroupItem>
              <ToggleGroupItem value="month" className="px-3">{t('PRICING.UPGRADE.MONTHLY')}</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-fee" className={labelClass}>{t('ADMIN_PLANS.EDIT.INTEGRATION_FEE')}</Label>
            <Input id="c-fee" type="number" min={0} className="tabular-nums" value={form.integrationFee} onChange={(e) => set('integrationFee', parseFloat(e.target.value) || 0)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="c-notice" className={labelClass}>{t('PRICING.CONTRACT.NOTICE')}</Label>
            <Input id="c-notice" type="number" min={0} className="tabular-nums" value={form.noticeDays} onChange={(e) => set('noticeDays', parseInt(e.target.value, 10) || 0)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label className={labelClass}>{t('PRICING.CONTRACT.AUTO_RENEW')}</Label>
            <div className="flex h-9 items-center">
              <Switch checked={form.autoRenew} onCheckedChange={(v) => set('autoRenew', v)} aria-label={t('PRICING.CONTRACT.AUTO_RENEW')} />
            </div>
          </div>
        </div>

        {tooShort && <p className="-mt-2 text-xs text-destructive">{t('PRICING.CONTRACT.MIN_TERM')}</p>}

        {form.lines.length > 0 && (
          <div className="rounded-lg border">
            <p className="border-b px-3 py-2 text-xs text-muted-foreground">{t('PRICING.CONTRACT.LINES')}</p>
            <ul className="divide-y text-sm">
              {form.lines.map((l) => (
                <li key={l.key} className="flex items-center justify-between gap-3 px-3 py-2">
                  <span className="min-w-0 truncate">{t(`PRICING.RATE.${l.key}.LABEL`, { defaultValue: l.label || l.key })}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{l.qty} × {money(l.unitPrice, form.currency)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="c-notes" className={labelClass}>{t('PRICING.CONTRACT.NOTES')}</Label>
          <Textarea id="c-notes" rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder={t('PRICING.CONTRACT.NOTES_PLACEHOLDER')} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <p className="text-xs text-muted-foreground">
            {form.monthlyAmount > 0
              ? t('PRICING.CONTRACT.VALUE', { amount: money(form.monthlyAmount * form.termMonths + form.integrationFee, form.currency), count: form.termMonths })
              : t('PRICING.CONTRACT.ACTIVE_HINT')}
          </p>
          <Button variant="outline" disabled={!dirty || saving || tooShort} onClick={save} data-testid="contract-save">
            {t('PRICING.CONTRACT.SAVE')}
          </Button>
        </div>
      </div>

      <Dialog open={calcOpen} onOpenChange={setCalcOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{t('PRICING.DEAL.TITLE')}</DialogTitle>
            <DialogDescription>{t('PRICING.DEAL.SUBTITLE')}</DialogDescription>
          </DialogHeader>
          <DealCalculator initial={initialDraft} onUse={fromCalculator} useLabel={t('PRICING.DEAL.USE')} />
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
